import React, {
    FunctionComponent,
    MouseEvent,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import formatDate from '@ps-int/ufo-helpers/lib/date/date-to-string';
import { ChatHelp } from '@yandex-360/iconostasis/dist/icons/ChatHelp';
import { Faq } from '@yandex-360/iconostasis/dist/icons/Faq';
import { History } from '@yandex-360/iconostasis/dist/icons/History';
import { TooltipStateful } from '@yandex-360/orb-components/dist/components/Tooltip';
import cn from 'classnames';
import { connect, ConnectedProps, useSelector } from 'react-redux';

import { RootState } from '../../../../init-store';
import { configSlice } from '../../features/config';
import { downloadLastRecordAction } from '../../features/download-record';
import { selectIsConferencesHistoryEnabled } from '../../features/flags/selectors';
import { setIsFaqModalVisible } from '../../features/settings';
import { openSettingsModalAction } from '../../features/settings-modal/slice';
import { isMobileUa } from '../../features/uatraits/selectors';
import { getExternalLinks } from '../../helpers/external-links';
import { i18n } from '../../helpers/i18n';
import { countMetrika } from '../../helpers/metrika';
import { replace, SCREENS } from '../../helpers/navigation';
import { openUrl } from '../../helpers/open-url';
import { useActions } from '../../hooks';
import { useTypedStore } from '../../hooks/useTypedStore';
import Button from '../button';
import { FaqModal } from '../faq-modal';
import SvgImage from '../svg-image';
import { ImageName } from '../svg-image/images';
import { FooterButton } from './footer-button';
import css from './index.css';
import { InstallMobileAppButton } from './installMobileAppButton';
import TermsOfUse from './terms-of-use';

const supportIcon = <SvgImage imageName="help2" />;
const recordIcon = <SvgImage imageName="record" />;
const settingsIcon = <SvgImage imageName="settings" />;
const historyIcon = <History variant="outline" size="md" />;
const faqIcon = <Faq variant="outline" size="md" />;
const helpIcon = <ChatHelp variant="outline" size="md" />;

const mapState = (state: RootState) => ({
    OSFamily: state.ua.OSFamily,
    agreementShown: state.config.agreementShown,
    hasLastRecording: state.recording.hasLastRecording,
    lastRecordingDate: state.recording.lastRecordingDate,
});

const mapDispatch = {
    setAgreementShown: configSlice.actions.setAgreementShown,
    downloadLastRecord: downloadLastRecordAction,
};

const connector = connect(mapState, mapDispatch);

type Props = ConnectedProps<typeof connector> & {
    hideSettings?: boolean;
};

const Footer: FunctionComponent<Props> = ({
    OSFamily,
    agreementShown,
    hideSettings = false,
    hasLastRecording,
    lastRecordingDate,
    setAgreementShown,
    downloadLastRecord,
}) => {
    const store = useTypedStore();
    const isMobile = useSelector(isMobileUa);

    const footerRef = useRef<HTMLDivElement>(null);

    const [windowWidth, setWindowWidth] = useState(0);

    const actions = useActions({ openSettingsModalAction, setIsFaqModalVisible });
    const onClickSettings = useCallback(
        (event: MouseEvent) => {
            actions.openSettingsModalAction({
                shouldHiddenSectionBeVisible: event.altKey,
            });
        },
        [actions]
    );

    useLayoutEffect(() => {
        const updateWindowWidth = () => {
            if (document.documentElement) {
                const { width } = document.documentElement.getBoundingClientRect();

                setWindowWidth(width);
            }
        };

        const ro = new ResizeObserver(updateWindowWidth);

        ro.observe(document.documentElement);

        return () => {
            ro.unobserve(document.documentElement);
            ro.disconnect();
        };
    }, []);

    useEffect(() => {
        return () => {
            setAgreementShown();
        };
    });

    const openHelp = useCallback(() => {
        countMetrika('main_screen', 'open_help');
        openUrl(getExternalLinks(store.getState()).help, '_blank');
    }, [store]);

    const clickInstallApp = useCallback(() => {
        countMetrika('main_screen', 'download_desktop');
    }, []);

    const downloadRecord = useCallback(() => {
        downloadLastRecord();
    }, [downloadLastRecord]);

    const appImageHash: Record<string, ImageName> = {
        MacOS: 'appleIcon',
        Windows: 'win',
    };

    const installAppImageName = appImageHash[OSFamily];

    const openFaqModal = () => {
        actions.setIsFaqModalVisible(true);
        countMetrika('main_screen', 'open_faq');
    };

    const installAppIcon = useMemo(() => {
        return installAppImageName ? <SvgImage imageName={installAppImageName} /> : undefined;
    }, [installAppImageName]);

    const isConferencesHistoryEnabled = useSelector(selectIsConferencesHistoryEnabled);

    if (isMobile) {
        return (
            <div className={css.footer} ref={footerRef}>
                <div className={css.helpWrapper}>
                    <Button
                        view="neutral"
                        size="sm"
                        iconLeft={supportIcon}
                        className={cn(css.footerButton, css.buttonSupport)}
                        onClick={openHelp}
                    >
                        {i18n('common', 'support')}
                    </Button>
                    {isConferencesHistoryEnabled ? (
                        <Button
                            view="neutral"
                            iconLeft={historyIcon}
                            className={cn(css.footerButton, css.conferencesHistoryButton)}
                            size="sm"
                            elementType="a"
                            onClick={() => {
                                countMetrika('main_screen', 'meeting_history_click');
                                replace(SCREENS.CONFERENCES_HISTORY);
                            }}
                        >
                            {i18n('conferences-history', 'conferences-history')}
                        </Button>
                    ) : hasLastRecording ? (
                        <TooltipStateful
                            content={i18n('record', 'last-record-download-hint', {
                                date: formatDate(lastRecordingDate || 0, 'DD.MM.YYYY HH-mm-ss'),
                            })}
                            trigger="hover"
                        >
                            <Button
                                testId="download-last-record-button"
                                view="neutral"
                                iconLeft={recordIcon}
                                className={cn(css.footerButton, css.lastRecordButton)}
                                onClick={downloadRecord}
                                size="sm"
                            >
                                {i18n('record', 'last-record-download')}
                            </Button>
                        </TooltipStateful>
                    ) : (
                        <Button
                            view="neutral"
                            icon={faqIcon}
                            className={cn(css.button, css.buttonFaq)}
                            onClick={openFaqModal}
                            size="sm"
                        >
                            {i18n('faq', 'title')}
                        </Button>
                    )}
                </div>
                <div className={css.filler} />
                {!hideSettings && (
                    <Button
                        testId="settings-button"
                        view="neutral"
                        size="sm"
                        className={cn(css.footerButton, css.buttonSettings)}
                        onClick={onClickSettings}
                        icon={settingsIcon}
                    />
                )}
                {!agreementShown && <TermsOfUse href={getExternalLinks(store.getState()).termsOfUse} />}
            </div>
        );
    }

    return (
        <div className={cn(css.desktopFooter, { [css.withText]: !agreementShown })} ref={footerRef}>
            <div className={css.buttonsWrapper}>
                <div className={css.buttonsGroup}>
                    <FooterButton
                        mode={windowWidth <= 1030 ? 'compact' : 'full'}
                        view="neutral"
                        size="md"
                        icon={helpIcon}
                        className={cn(css.footerButton, css.buttonSupport)}
                        onClick={openHelp}
                        content={i18n('common', 'support')}
                    />
                    <FooterButton
                        mode={windowWidth <= 1030 ? 'compact' : 'full'}
                        className={cn(css.footerButton, css.buttonFaq)}
                        view="neutral"
                        size="md"
                        icon={faqIcon}
                        onClick={openFaqModal}
                        content={i18n('faq', 'title')}
                    />
                    {isConferencesHistoryEnabled && (
                        <FooterButton
                            mode={windowWidth <= 480 ? 'compact' : 'full'}
                            content={i18n('conferences-history', 'conferences-history')}
                            view="neutral"
                            icon={historyIcon}
                            className={cn(css.footerButton, css.conferencesHistoryButton)}
                            size="md"
                            elementType="a"
                            onClick={() => {
                                countMetrika('main_screen', 'meeting_history_click');
                                replace(SCREENS.CONFERENCES_HISTORY);
                            }}
                        />
                    )}
                    {hasLastRecording && (
                        <FooterButton
                            mode={windowWidth <= 1160 ? 'compact' : 'full'}
                            content={i18n('record', 'last-record-download')}
                            tooltip={i18n('record', 'last-record-download-hint', {
                                date: formatDate(lastRecordingDate || 0, 'DD.MM.YYYY HH-mm-ss'),
                            })}
                            testId="download-last-record-button"
                            view="neutral"
                            icon={recordIcon}
                            className={cn(css.footerButton, css.lastRecordButton)}
                            onClick={downloadRecord}
                            size="md"
                        />
                    )}
                </div>
                {installAppImageName && (
                    <div className={css.buttonsGroup}>
                        <FooterButton
                            mode={windowWidth <= 878 ? 'compact' : 'full'}
                            view="neutral"
                            elementType="a"
                            href="/download-desktop"
                            target="_blank"
                            className={cn(css.footerButton, css.buttonInstallApp)}
                            icon={installAppIcon}
                            onClick={clickInstallApp}
                            size="md"
                            content={i18n('common', 'install-app-v4', { osFamily: OSFamily })}
                        />
                        <InstallMobileAppButton
                            mode={windowWidth <= 878 ? 'compact' : 'full'}
                            onClick={() => countMetrika('main_screen', 'install_mobile_app')}
                        />
                    </div>
                )}
            </div>
            {!agreementShown && <TermsOfUse href={getExternalLinks(store.getState()).termsOfUse} />}
            <FaqModal />
        </div>
    );
};

export default connector(Footer);
